import Button, { ButtonProps } from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { forwardRef, memo, Ref } from 'react';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'inline-flex',
    position: 'relative',
  },
  progress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginLeft: theme.spacing(-1.5),
    marginTop: theme.spacing(-1.5),
  },
  purpleButton: {
    color: 'white',
    backgroundColor: theme.palette.accent.main,
    '&:hover': {
      backgroundColor: theme.palette.accent.dark,
    },
  },
  purpleProgress: {
    color: theme.palette.accent.main,
  },
  redButton: {
    color: 'white',
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  redProgress: {
    color: theme.palette.error.main,
  },
  greenButton: {
    color: 'white',
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  greenProgress: {
    color: theme.palette.success.main,
  },
}));

export type ProgressButtonProps = ButtonProps & {
  /**
   * If `true', shows an infinite progress bar and disables the button.
   */
  inProgress: boolean;
  meshColor?: string;
};

function ProgressButtonComponent(
  props: ProgressButtonProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const classes = useStyles();
  const {
    inProgress,
    disabled,
    children,
    variant,
    className,
    meshColor,
    ...otherProps
  } = props;

  return (
    <div className={clsx(classes.wrapper, className)} ref={ref}>
      <Button
        disabled={disabled || inProgress}
        variant={variant}
        {...otherProps}
        fullWidth
        className={clsx({
          [classes.purpleButton]: meshColor === 'purple',
          [classes.redButton]: meshColor === 'red',
          [classes.greenButton]: meshColor === 'green',
        })}
      >
        {children}
      </Button>
      {inProgress && (
        <CircularProgress
          color="primary"
          className={clsx(classes.progress, {
            [classes.purpleProgress]: meshColor === 'purple',
            [classes.redProgress]: meshColor === 'red',
            [classes.greenProgress]: meshColor === 'green',
          })}
          size={24}
        />
      )}
    </div>
  );
}

export const ProgressButton = memo(
  forwardRef(ProgressButtonComponent),
) as typeof ProgressButtonComponent;
